import { Context } from '@front10/helpers/dist/Context';
import type Leaflet from 'leaflet';
import React, { FunctionComponent, useContext, useEffect, useRef } from 'react';
import useStyles from '@front10/helpers/dist/UseStyles';
import type ReactLeaflet from 'react-leaflet';
import type { Marker, Popup } from 'react-leaflet';
import FareItemCard from '../../../items/FareItemCard';
import dayjs from 'dayjs';
import {
  DestinationMarkerData,
  FareMarkerData,
  isFareMarker,
  isNoFareMarker
} from '../classes/MarkerData';
import Button from '../../../elements/Button';
import Icon from '../../../elements/Icon';
import CheckAvailabilityCard from './CheckAvailabilityCard';
import LoadingDestination from './LoadingDestination';
import { NoFareMarkerData } from './../classes/MarkerData';
import { strFormatV2 } from '../../../../helpers/utils';

export interface EndRouteMarkerProps {
  marker: DestinationMarkerData;
  iconSettings?: $TSFixMe;
  iconSettingsStyles?: $TSFixMe;
  loadingSettings?: $TSFixMe;
  checkAvailabilityCardSettings?: $TSFixMe;
  fareItemCardSettings?: $TSFixMe;
  isMobile?: boolean;
  onClickCloseBtn?: () => void;
  popupSettings: $TSFixMe;
  tooltipSettings: $TSFixMe;
  labelStructure?: string;
}

export type EndRouteMarkerPropsType = EndRouteMarkerProps;

const defaultFareItemProps = {
  showDate: false,
  showBackgroundImage: false,
  showMiles: false,
  showPill: false,
  showDurationTrip: false,
  fareItemPrice: {
    showPricePromo: false
  },
  showDepartureReturnBeforePrice: true,
  showPrice: true,
  bgColor: 'dark',
  textColor: 'white'
};

const defaultFareItemActiveProps = {
  ...defaultFareItemProps,
  showHorizontalSeparator: true,
  showCallToAction: true,
  tabIndexCallToAction: false
};

const EndRouteMarker: FunctionComponent<EndRouteMarkerPropsType> = (
  props: EndRouteMarkerPropsType
) => {
  const {
    marker,
    iconSettings = {},
    iconSettingsStyles = {},
    loadingSettings = {},
    checkAvailabilityCardSettings = {},
    fareItemCardSettings = {},
    isMobile,
    popupSettings = {},
    tooltipSettings = {},
    ...markerSettings
  } = props;
  const labelStructure =
    checkAvailabilityCardSettings.labelStructure === undefined
      ? '{city.name} ({iataCode})'
      : checkAvailabilityCardSettings.labelStructure;

  const markerRef = useRef<Marker>(null);
  const popupRef = useRef<Popup>(null);
  const fareItemCardReference = useRef<$TSFixMe>(null);
  const { translate } = useContext(Context);
  const { direction, theme } = useContext(Context);

  useEffect(() => {
    markerRef.current?.leafletElement.openPopup();
    popupRef.current?.leafletElement.bringToFront();
  });

  useEffect(() => {
    if (isMobile && marker && fareItemCardReference && fareItemCardReference.current) {
      // TODO: This warning is the result of the render that is done when a fare is
      //       selected, it must be seen if later it makes you think of a better fix
      // Warning: unstable_flushDiscreteUpdates: Cannot flush updates when React is already rendering.
      fareItemCardReference.current.focus();
    }
  });

  const { Marker, Popup, Tooltip } = require('react-leaflet') as typeof ReactLeaflet;
  const leaflet = require('leaflet') as typeof Leaflet;

  const [showToolTip, toggleTooltip] = React.useState(true);
  const [iconClassName] = useStyles(iconSettingsStyles, { direction, theme });
  const icon = leaflet.divIcon({
    className: 'custom-div-icon',
    html: `<div class='marker-pin mp-endroute-selected ${iconClassName}' ></div>`,
    iconSize: [20, 20],
    ...iconSettings
  });

  const getNewLabel = (marker: any, labelStructure: string) => {
    return strFormatV2(labelStructure, marker);
  };
  const defaultPopupsProps = {
    direction: 'top',
    autoClose: false,
    closeOnClick: false, // TODO: This was null. Why?
    closeOnEscapeKey: false,
    closeButton: false,
    autoPan: false,
    className: 'end-route-popup',
    ...popupSettings
  } as Leaflet.PopupOptions;

  const defaultTooltipsProps = {
    direction: 'auto',
    interactive: true,
    opacity: 1,
    className: 'end-route-popup',
    ...tooltipSettings
  } as Leaflet.TooltipOptions;

  const renderLoading = marker.loading && (
    <Popup {...defaultPopupsProps} ref={popupRef}>
      <LoadingDestination
        {...loadingSettings}
        label={translate('cmp-interactive-map-container-loading-destination-text', 'Loading')}
      />
    </Popup>
  );
  const renderCheckAvailability = isNoFareMarker(marker) && !marker.loading && (
    <Tooltip {...defaultTooltipsProps} permanent={showToolTip}>
      <CheckAvailabilityCard
        onClickCloseBtn={() => {
          toggleTooltip(!showToolTip);
          props.onClickCloseBtn?.();
        }}
        {...checkAvailabilityCardSettings}
        containerSettings={{
          className: `check-availability-container openFareItemCard`,
          ...checkAvailabilityCardSettings.containerSettings
        }}
        fareItemSettings={{
          data: {
            dataType: 'EMPTY_FROM_API',
            origin: marker.origin.value,
            destination: marker.value,
            journeyType: marker.journeyType ? marker.journeyType : 'ROUND_TRIP',
            departureDate:
              (marker as NoFareMarkerData & { departureDate: string }).departureDate !== undefined
                ? dayjs(
                    (marker as NoFareMarkerData & { departureDate: string }).departureDate
                  ).format('YYYY-MM-DD')
                : '',
            returnDate:
              (marker as NoFareMarkerData & { returnDate: string }).returnDate !== undefined
                ? dayjs((marker as NoFareMarkerData & { returnDate: Date }).returnDate).format(
                    'YYYY-MM-DD'
                  )
                : ''
          },
          fareItemNoPriceAvailable: {
            showLinkAvailability: true,
            ...(checkAvailabilityCardSettings.fareItemSettings
              ? checkAvailabilityCardSettings.fareItemSettings.fareItemNoPriceAvailable
              : {})
          },
          ...defaultFareItemActiveProps,

          ...checkAvailabilityCardSettings.fareItemSettings
        }}
        originDestinationSettings={{
          ...{
            destinationCity: getNewLabel(marker, labelStructure),

            originCity: marker.origin.label,
            origin: marker.value,
            destination: marker.value,
            showDestinationAirportCode: false
          },
          ...checkAvailabilityCardSettings.originDestinationSettings
        }}
      />
    </Tooltip>
  );

  const baseCloseBtnStyle = {
    'cmp-styles': {
      position: 'absolute',
      top: '0px',
      'z-Index': '1',
      right: '0px',
      'background-color': 'transparent !important',
      border: 'unset',
      margin: '1px'
    }
  };

  const renderActiveFare = isFareMarker(marker) && !marker.loading && (
    <Tooltip {...defaultTooltipsProps} permanent={showToolTip}>
      {(props.fareItemCardSettings.showCloseButton ||
        props.fareItemCardSettings.showCloseButton === undefined) && (
        <Button
          {...baseCloseBtnStyle}
          ariaLabel={translate('cmp-interactive-map-fare-card-close', 'Close')}
          color="primary"
          type="button"
          {...props.fareItemCardSettings.closeButtonSettings}
          onClick={() => {
            toggleTooltip(!showToolTip);
            props.onClickCloseBtn?.();
          }}
          tabIndex="-1"
          ariaHidden={true}
        >
          <Icon icon={'close'} {...props.fareItemCardSettings.closeBtnIconSettings} />
        </Button>
      )}
      <FareItemCard
        data={(marker as FareMarkerData).fare}
        focusReferences={(ref: any) => (fareItemCardReference.current = ref)}
        {...defaultFareItemActiveProps}
        {...fareItemCardSettings}
        ariaHidden={true}
        className="openFareItemCard"
      />
    </Tooltip>
  );
  return (
    <Marker
      position={marker.location}
      icon={icon}
      ref={markerRef}
      interactive={false}
      keyboard={false}
      {...markerSettings}
    >
      {renderLoading}
      {renderCheckAvailability}
      {renderActiveFare}
    </Marker>
  );
};

export default React.memo(EndRouteMarker, (prevProps, nextProps) => {
  return prevProps.marker === nextProps.marker;
});
